'use client';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  CircularProgress,
  Link as MuiLink,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  fetchInventoryAuditorCountSnapshotRecent,
  type InventoryAuditorCountSnapshotRecentRow,
} from '@/hooks/useInventoryAuditorInventory';
import { useNotification } from '@/contexts/NotificationContext';

function formatIaCountDateLabel(ymd: string): string {
  const parts = ymd.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return ymd;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const InventoryAuditorRecentCountReportsSection: React.FC = () => {
  const { showError } = useNotification();
  const [rows, setRows] = useState<InventoryAuditorCountSnapshotRecentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const r = await fetchInventoryAuditorCountSnapshotRecent({ skip: 0, take: 10 });
        if (!cancelled) {
          setRows(r.data);
          setTotal(r.total);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          showError(e instanceof Error ? e.message : 'Failed to load recent count reports');
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showError]);

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6" component="h2">
          Recent count reports
        </Typography>
        <MuiLink
          component={RouterLink}
          to="/app/inventory-auditor/locations"
          variant="body2"
          underline="hover"
        >
          All locations
        </MuiLink>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No saved store counts yet. Run a full store count at a location to create a report.
        </Typography>
      ) : (
        <>
          {total > rows.length && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Showing {rows.length} of {total} count days across locations.
            </Typography>
          )}
          <List disablePadding>
            {rows.map((row) => (
              <ListItemButton
                key={`${row.locationId}-${row.countDate}`}
                component={RouterLink}
                to={`/app/inventory-auditor/locations/${row.locationId}?tab=history&countDate=${encodeURIComponent(row.countDate)}`}
                sx={{ borderRadius: 1, mb: 0.5, px: 1 }}
              >
                <ListItemText
                  primary={row.locationName || `Location #${row.locationId}`}
                  secondary={`${formatIaCountDateLabel(row.countDate)} · ${row.lineCount} line${row.lineCount === 1 ? '' : 's'}`}
                />
                <ChevronRightIcon fontSize="small" color="action" />
              </ListItemButton>
            ))}
          </List>
        </>
      )}
    </Box>
  );
};

export default InventoryAuditorRecentCountReportsSection;
